import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bot,
  Cable,
  Globe,
  LayoutGrid,
  Mail,
  Megaphone,
  MessageCircleMore,
  Printer,
  Search,
  ShieldCheck,
  Share2,
  Smartphone,
  Store,
  Target,
  Truck,
  Workflow,
} from 'lucide-react';

export const billingTabs = [
  'Development',
  'Marketing',
  'Maintenance + Hosting',
] as const;

export type BillingTab = (typeof billingTabs)[number];

export const marketingDurationTabs = ['Monthly', 'Half-Yearly', 'Yearly'] as const;
export type MarketingDurationTab = (typeof marketingDurationTabs)[number];

export const maintenanceDurationTabs = ['Monthly', '6 Months', '12 Months'] as const;
export type MaintenanceDurationTab = (typeof maintenanceDurationTabs)[number];

export type Plan = {
  id: string;
  category: string;
  name: string;
  subtitle: string;
  priceLabel: string;
  priceSuffix?: string;
  billingNote: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  iconClassName?: string;
  featured?: boolean;
  primaryAction: string;
  existingAction?: string;
  infoNote?: string;
  secondarySectionHeading?: string;
  features: Array<{ label: string; info?: string }>;
  addons?: Array<{ label: string; price: string; info?: string }>;
};

export type AddonCard = {
  title: string;
  description: string;
  price: string;
  icon: LucideIcon;
};

export type ComparisonValue = 'check' | 'dash' | 'optional';

export type ComparisonConfig = {
  headers: [string, string, string, string];
  rows: Array<{
    label: string;
    values: [ComparisonValue, ComparisonValue, ComparisonValue, ComparisonValue];
  }>;
};

const whatsappApiTooltip =
  'If you purchase this plan via AiWizChat, WhatsApp platform API integration is included at no setup cost. If you use another provider, integration setup is charged separately at ₹5,000. Meta and partner messaging charges are billed separately on actual usage.';

const externalAppIntegrationTooltip =
  'Google Calendar, Shopify, n8n, Google Meet, Google Sheets or Excel and similar third-party apps can be connected at ₹8,000+ per app. If WhatsApp is required on a non-AiWizChat provider, the setup cost is ₹5,000.';

const digitalKycTooltip =
  'Includes digital verification workflow planning for PAN, GST, bank-account and company-document checks. Aadhaar or face verification can be added where supported by the selected KYC provider and compliance scope.';

const hardwareIntegrationTooltip =
  'Security and access-control device integration for hardware-led workflows. Starting scope covers one device flow; hardware procurement, OEM subscription and field installation are billed separately.';

const cloudPrinterTooltip =
  'Connect one cloud printer across multiple devices with super-admin control for print approvals, queue handling, timed prints and centralized access management.';

const metaLongTermTooltip =
  'CRM setup support, advanced analytics, remarketing structure, bid refinement, brand authorization support and agency ad-account coordination are included in half-yearly and yearly Meta Ads plans.';

const googleLongTermTooltip =
  'CRM setup support, advanced analytics, conversion cleanup, remarketing audiences, brand account access and performance bid refinement are included in half-yearly and yearly Google Ads plans.';

const linkedinLongTermTooltip =
  'LinkedIn lead workflow support, advanced audience layering, reporting refinement and CRM-ready handoff are included in half-yearly and yearly plans.';

const organicLongTermTooltip =
  'Content bank planning, brand tone system, monthly strategy review and stronger reporting structure are included in half-yearly and yearly organic plans.';

export const processStepsByBilling: Record<BillingTab, string[]> = {
  Development: ['Market Research', 'Strategy', 'Development', 'Launch', 'Optimization'],
  Marketing: ['Research', 'Campaign Setup', 'Launch', 'Optimize', 'Scale'],
  'Maintenance + Hosting': ['Audit', 'Support', 'Secure', 'Monitor', 'Improve'],
};

const developmentPlans: Plan[] = [
  {
    id: 'website',
    category: 'Website Setup',
    name: 'E-commerce Website',
    subtitle: 'For stores that want to launch and sell fast',
    priceLabel: '₹18,000',
    priceSuffix: '/ project',
    billingNote: 'Starting project cost',
    description:
      'Premium e-commerce website setup with conversion-focused checkout, secure payments, order tracking and admin-side analytics.',
    icon: Globe,
    accent: 'from-slate-950 via-slate-900 to-blue-900',
    iconClassName: 'text-white',
    primaryAction: 'Purchase Now',
    existingAction: 'Add in Existing',
    infoNote:
      'Starting price is for a standard e-commerce setup. Final cost depends on design scope, product volume, checkout logic and custom workflow requirements.',
    features: [
      { label: 'Product listing and management' },
      { label: 'Product detail pages and trust blocks' },
      { label: 'Smart search and product filters' },
      { label: 'Add to Cart and Wishlist' },
      { label: 'Guest checkout and account section' },
      { label: 'Offer banners, featured sections and coupon-ready flow' },
      { label: 'UPI, card, net banking and COD options' },
      { label: 'Free WhatsApp Business API', info: whatsappApiTooltip },
      { label: 'Order tracking, My Orders and status flow' },
      { label: 'Basic SEO and speed-ready storefront setup' },
      {
        label: 'Admin analytics dashboard',
        info: 'Tracks sales value, customer behavior, traffic trend, product trend and order performance.',
      },
      { label: 'Order management panel' },
    ],
    addons: [
      {
        label: 'AI Chat Agent',
        price: '₹5,000+',
        info: 'Handles order-status questions, surfaces relevant product lists and can assist with payment journeys. API usage is billed separately.',
      },
      {
        label: 'Shipping Integration',
        price: '₹4,000+',
        info: 'Automatically pushes order data into your shipping panel and syncs delivery status back to customer-facing flows.',
      },
      {
        label: 'Smart AI Search',
        price: '₹5,000+',
        info: 'AI-powered product search helps customers discover relevant products faster through natural-language queries.',
      },
      {
        label: 'Reviews & Social Proof',
        price: '₹3,500+',
        info: 'Add product ratings, review blocks, testimonial widgets and trust-building proof sections.',
      },
    ],
  },
  {
    id: 'app',
    category: 'Mobile App',
    name: 'Android + iOS App',
    subtitle: 'For brands ready to sell through mobile-first journeys',
    priceLabel: '₹35,000',
    priceSuffix: '/ project',
    billingNote: 'Starting development cost',
    description:
      'Commerce-ready Android and iOS app setup with customer journeys, checkout flow, notifications and analytics-ready events.',
    icon: Smartphone,
    accent: 'from-white via-sky-50 to-violet-50',
    primaryAction: 'Purchase Now',
    existingAction: 'Add in Existing',
    infoNote:
      'Final pricing depends on backend readiness, native-device needs, payment stack, publishing scope and custom mobile modules.',
    features: [
      { label: 'Android and iOS app UI setup' },
      { label: 'Home, category, search and product-detail screens' },
      { label: 'User login, profile and saved-address flow' },
      { label: 'Wishlist, offers and saved-product journeys' },
      { label: 'Cart, checkout and payment-ready journey' },
      { label: 'Order history and status tracking' },
      { label: 'Push notifications and deep-link-ready journeys' },
      { label: 'Free WhatsApp Business API', info: whatsappApiTooltip },
      {
        label: 'Advanced analytics included',
        info: 'App event planning includes installs, product views, cart actions, checkout events and retention-ready tracking.',
      },
      { label: 'Coupon, banner and repeat-purchase flow support' },
      {
        label: 'API integration with existing backend',
        info: 'If your backend already exists we connect the app to it; if backend scope is missing, that scope is estimated separately.',
      },
      { label: 'App store launch guidance' },
    ],
    addons: [
      {
        label: 'AI Chat Agent',
        price: '₹5,000+',
        info: 'Adds conversational product discovery, support prompts and payment assistance inside your app funnel.',
      },
      {
        label: 'Shipping Panel Sync',
        price: '₹4,000+',
        info: 'Pushes app orders to the shipping provider and supports delivery-status sync for the app experience.',
      },
      {
        label: 'Extra Third-party App Integration',
        price: '₹8,000+',
        info: 'For custom app connections such as loyalty tools, ERP bridges, review systems or special external service integrations.',
      },
      {
        label: 'Loyalty / Wallet Module',
        price: '₹8,000+',
        info: 'Add wallet balance, reward points, cashback or repeat-purchase retention journeys inside the app.',
      },
      {
        label: 'Referral / Membership Module',
        price: '₹10,000+',
        info: 'Supports invite rewards, member access, special pricing tiers or paid-membership style app journeys.',
      },
    ],
  },
  {
    id: 'crm',
    category: 'CRM Automation',
    name: 'CRM + Sales Workflow',
    subtitle: 'Best for leads, follow-ups and team visibility',
    priceLabel: '₹20,000',
    priceSuffix: '/ project',
    billingNote: 'Starting setup cost',
    description:
      'Sales CRM setup with automation, lead tracking, reminders, reporting and structured team follow-up workflows.',
    icon: Workflow,
    accent: 'from-emerald-500 via-green-500 to-lime-500',
    featured: true,
    primaryAction: 'Purchase Now',
    existingAction: 'Add in Existing',
    infoNote:
      'Works well for consultation, service, course, real-estate, education and internal sales teams that need lead discipline.',
    features: [
      { label: 'CRM pipeline and stage setup' },
      { label: 'Lead capture forms and source tagging' },
      { label: 'Lead assignment and follow-up task flow' },
      { label: 'WhatsApp and email reminder automation' },
      { label: 'Free WhatsApp Business API', info: whatsappApiTooltip },
      { label: 'Lead notes, activity timeline and sales visibility' },
      { label: 'Payment or booking-link automation' },
      { label: 'Role-based access for sales team members' },
      {
        label: 'Sales dashboard and reporting',
        info: 'Can cover lead status, follow-up aging, source quality, booking value and team performance views.',
      },
      { label: 'Handover-ready workflow for operations or support' },
    ],
    addons: [
      {
        label: 'External App Integration',
        price: '₹8,000+',
        info: externalAppIntegrationTooltip,
      },
      {
        label: 'Security Access Hardware Integration',
        price: '₹25,000+',
        info: hardwareIntegrationTooltip,
      },
      {
        label: 'Cloud Printer Control',
        price: '₹45,000+',
        info: cloudPrinterTooltip,
      },
    ],
  },
  {
    id: 'marketplace',
    category: 'Marketplace Build',
    name: 'Multi-vendor Marketplace',
    subtitle: 'For platforms managing sellers, products and orders',
    priceLabel: '₹60,000',
    priceSuffix: '/ project',
    billingNote: 'Starting project cost',
    description:
      'Scalable multi-vendor marketplace build with seller onboarding, admin controls, order routing and verification-ready operations.',
    icon: Store,
    accent: 'from-white via-orange-50 to-amber-50',
    primaryAction: 'Purchase Now',
    existingAction: 'Book Consultant',
    infoNote:
      'Final quote depends on seller roles, commission logic, KYC provider, payout stack, shipping workflow and custom marketplace modules.',
    features: [
      { label: 'Vendor registration and approval flow' },
      { label: 'Seller dashboard with product management' },
      { label: 'Multi-vendor storefront, search and filters' },
      { label: 'Marketplace cart, checkout and order routing' },
      { label: 'Commission logic and payout-ready workflow' },
      { label: 'Digital KYC verification flow', info: digitalKycTooltip },
      { label: 'Free WhatsApp Business API', info: whatsappApiTooltip },
      { label: 'Vendor order tracking and support workflow' },
      { label: 'Admin, vendor and operations role controls' },
      {
        label: 'Marketplace analytics dashboard',
        info: 'Covers seller activity, order trend, payout visibility, catalog growth and operational performance.',
      },
      { label: 'Shipping and fulfillment-ready architecture' },
      { label: 'Growth-ready integration and API planning' },
    ],
    addons: [
      {
        label: 'External App Integration',
        price: '₹8,000+',
        info: externalAppIntegrationTooltip,
      },
      {
        label: 'Security Access Hardware Integration',
        price: '₹25,000+',
        info: hardwareIntegrationTooltip,
      },
      {
        label: 'Cloud Printer Control',
        price: '₹45,000+',
        info: cloudPrinterTooltip,
      },
      {
        label: 'Shipping Carrier Automation',
        price: '₹12,000+',
        info: 'Connects marketplace orders with fulfillment, shipping labels, status sync and operational routing logic.',
      },
    ],
  },
];

const marketingPlansByDuration: Record<MarketingDurationTab, Plan[]> = {
  Monthly: [
    {
      id: 'meta-ads-monthly',
      category: 'Meta Ads',
      name: 'Meta Ads Growth',
      subtitle: 'For lead generation, remarketing and conversion campaigns',
      priceLabel: '₹10,000',
      priceSuffix: '/ month',
      billingNote: 'Starting monthly management fee',
      description:
        'Meta Ads management for Facebook and Instagram campaigns with audience testing, budget control and conversion-focused optimization.',
      icon: Megaphone,
      accent: 'from-[#0f172a] via-[#1d4ed8] to-[#2563eb]',
      iconClassName: 'text-white',
      featured: true,
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in 6 / 12 month plans',
      infoNote:
        'Ad spend is separate. Monthly plan is best for brands testing or restarting performance campaigns.',
      features: [
        { label: 'Business Manager and ad account review' },
        { label: 'Meta Pixel / CAPI event review' },
        { label: 'Campaign structure for leads, sales or traffic' },
        { label: 'Audience research and segmentation planning' },
        { label: 'Creative direction and ad copy guidance' },
        { label: 'Daily budget pacing and optimization' },
        { label: 'Weekly update and monthly reporting' },
        { label: 'Agency ad-account coordination support' },
      ],
      addons: [
        { label: 'CRM setup support', price: 'Included', info: metaLongTermTooltip },
        { label: 'Advanced analytics dashboard', price: 'Included', info: metaLongTermTooltip },
        { label: 'Remarketing audience structure', price: 'Included', info: metaLongTermTooltip },
        { label: 'Bid refinement and scaling layer', price: 'Included', info: metaLongTermTooltip },
        { label: 'Brand authorization support', price: 'Included', info: metaLongTermTooltip },
      ],
    },
    {
      id: 'google-ads-monthly',
      category: 'Google Ads',
      name: 'Google Ads Performance',
      subtitle: 'For search-intent traffic, conversions and high-buying users',
      priceLabel: '₹10,000',
      priceSuffix: '/ month',
      billingNote: '₹8,000 one-time setup + monthly management',
      description:
        'Google Ads management for Search, Performance Max and remarketing-ready campaigns with keyword and conversion optimization.',
      icon: Search,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in 6 / 12 month plans',
      infoNote:
        'Best for businesses that want intent-driven traffic from Google Search and stronger conversion quality.',
      features: [
        { label: 'Account structure and campaign setup' },
        { label: 'Keyword planning and search-theme mapping' },
        { label: 'Ad copy testing and extension setup' },
        { label: 'Negative keyword cleanup and filtering' },
        { label: 'Conversion tracking review and optimization' },
        { label: 'Bid strategy and device/location refinement' },
        { label: 'Monthly insights and search-query analysis' },
        { label: 'Performance Max or Search campaign handling' },
      ],
      addons: [
        { label: 'CRM setup support', price: 'Included', info: googleLongTermTooltip },
        { label: 'Advanced analytics dashboard', price: 'Included', info: googleLongTermTooltip },
        { label: 'Remarketing audience setup', price: 'Included', info: googleLongTermTooltip },
        { label: 'Conversion cleanup support', price: 'Included', info: googleLongTermTooltip },
        { label: 'Brand account access support', price: 'Included', info: googleLongTermTooltip },
      ],
    },
    {
      id: 'linkedin-ads-monthly',
      category: 'LinkedIn Ads',
      name: 'LinkedIn B2B Ads',
      subtitle: 'For high-ticket B2B leads, founders, CXOs and professional audiences',
      priceLabel: '₹15,000',
      priceSuffix: '/ month',
      billingNote: 'Starting monthly management fee',
      description:
        'LinkedIn Ads management for B2B demand generation, lead forms, job-title targeting and decision-maker outreach campaigns.',
      icon: Target,
      accent: 'from-slate-950 via-cyan-800 to-sky-600',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in 6 / 12 month plans',
      infoNote:
        'Best suited for consultants, agencies, SaaS, enterprise services and B2B growth teams.',
      features: [
        { label: 'Campaign objective and funnel planning' },
        { label: 'Job title, company and industry targeting' },
        { label: 'Lead gen forms or website conversion setup' },
        { label: 'Audience exclusions and quality filters' },
        { label: 'Creative direction for B2B messaging' },
        { label: 'Weekly optimization and CPL review' },
        { label: 'Lead-quality reporting and performance updates' },
        { label: 'Retargeting-ready campaign structure' },
      ],
      addons: [
        { label: 'CRM lead handoff support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Advanced reporting layer', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Audience refinement and retargeting', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Lead-quality workflow support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Brand asset and account support', price: 'Included', info: linkedinLongTermTooltip },
      ],
    },
    {
      id: 'organic-marketing-monthly',
      category: 'Organic Marketing',
      name: 'Social Media + SEO',
      subtitle: 'For daily brand handling across major social and local business platforms',
      priceLabel: '₹8,000',
      priceSuffix: '/ month',
      billingNote: 'Starting monthly handling fee',
      description:
        'Organic marketing support covering content handling, social presence and external SEO-ready writing for brand consistency.',
      icon: Share2,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in 6 / 12 month plans',
      infoNote:
        'Best for brands that want regular posting, profile handling and a stronger visible online presence.',
      features: [
        { label: 'Facebook page and account handling' },
        { label: 'Instagram handling and post coordination' },
        { label: 'LinkedIn page handling' },
        { label: 'Google Business Profile handling' },
        { label: 'Content calendar and caption planning' },
        { label: 'External SEO article writing support' },
        { label: 'Basic monthly reporting and page hygiene' },
        { label: 'Brand post coordination across platforms' },
      ],
      addons: [
        { label: 'Brand tone and content system', price: 'Included', info: organicLongTermTooltip },
        { label: 'Monthly strategy review', price: 'Included', info: organicLongTermTooltip },
        { label: 'Content bank planning', price: 'Included', info: organicLongTermTooltip },
        { label: 'Extended reporting support', price: 'Included', info: organicLongTermTooltip },
        { label: 'Campaign creative alignment', price: 'Included', info: organicLongTermTooltip },
      ],
    },
  ],
  'Half-Yearly': [
    {
      id: 'meta-ads-half-yearly',
      category: 'Meta Ads',
      name: 'Meta Ads Growth',
      subtitle: 'For brands that want stronger optimization over a sustained 6-month cycle',
      priceLabel: '₹32,000',
      priceSuffix: '/ 6 months',
      billingNote: '6-month management plan',
      description:
        'Longer Meta Ads plan with continuity for testing, optimization, reporting depth and better campaign learning.',
      icon: Megaphone,
      accent: 'from-[#0f172a] via-[#1d4ed8] to-[#2563eb]',
      iconClassName: 'text-white',
      featured: true,
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this 6-month plan',
      features: [
        { label: 'Everything from monthly Meta Ads handling' },
        { label: 'Long-term audience learning and testing' },
        { label: 'Creative iteration planning support' },
        { label: 'Stronger retargeting and funnel cleanup' },
        { label: 'Better cost-per-result stabilization' },
        { label: 'Priority optimization rhythm' },
      ],
      addons: [
        { label: 'CRM support free', price: 'Included', info: metaLongTermTooltip },
        { label: 'Advanced analytics', price: 'Included', info: metaLongTermTooltip },
        { label: 'Bidding refinement', price: 'Included', info: metaLongTermTooltip },
        { label: 'Remarketing setup', price: 'Included', info: metaLongTermTooltip },
        { label: 'Brand authorization and agency access', price: 'Included', info: metaLongTermTooltip },
      ],
    },
    {
      id: 'google-ads-half-yearly',
      category: 'Google Ads',
      name: 'Google Ads Performance',
      subtitle: 'For serious search advertisers who want continuity and cleaner conversion control',
      priceLabel: '₹50,000',
      priceSuffix: '/ 6 months',
      billingNote: 'Setup included in this 6-month plan',
      description:
        '6-month Google Ads plan for businesses that want stable search management, tracking control and performance tuning.',
      icon: Search,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this 6-month plan',
      features: [
        { label: 'Everything from monthly Google Ads handling' },
        { label: 'Search-query cleanup and trend learning' },
        { label: 'Account structure refinement over time' },
        { label: 'Bid and conversion optimization depth' },
        { label: 'Remarketing structure improvement' },
        { label: 'Longer-term reporting and improvement cycle' },
      ],
      addons: [
        { label: 'CRM support free', price: 'Included', info: googleLongTermTooltip },
        { label: 'Advanced analytics', price: 'Included', info: googleLongTermTooltip },
        { label: 'Bidding refinement', price: 'Included', info: googleLongTermTooltip },
        { label: 'Remarketing support', price: 'Included', info: googleLongTermTooltip },
        { label: 'Brand account access support', price: 'Included', info: googleLongTermTooltip },
      ],
    },
    {
      id: 'linkedin-ads-half-yearly',
      category: 'LinkedIn Ads',
      name: 'LinkedIn B2B Ads',
      subtitle: 'For B2B brands that need repeated testing and better lead-quality learning',
      priceLabel: '₹84,000',
      priceSuffix: '/ 6 months',
      billingNote: '6-month B2B demand-gen plan',
      description:
        'Longer LinkedIn Ads engagement designed for enterprise targeting, audience learning and lead-quality improvement.',
      icon: Target,
      accent: 'from-slate-950 via-cyan-800 to-sky-600',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this 6-month plan',
      features: [
        { label: 'Everything from monthly LinkedIn Ads handling' },
        { label: 'Message testing and audience refinement' },
        { label: 'Multi-offer B2B campaign support' },
        { label: 'Lead-quality optimization loop' },
        { label: 'Retargeting and nurture alignment' },
        { label: 'Extended reporting rhythm' },
      ],
      addons: [
        { label: 'CRM handoff support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Advanced reporting layer', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Audience refinement', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Lead-quality workflow support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Brand asset and access support', price: 'Included', info: linkedinLongTermTooltip },
      ],
    },
    {
      id: 'organic-marketing-half-yearly',
      category: 'Organic Marketing',
      name: 'Social Media + SEO',
      subtitle: 'For brands that want consistent platform growth with lower long-term handling cost',
      priceLabel: '₹42,000',
      priceSuffix: '/ 6 months',
      billingNote: '6-month handling plan',
      description:
        'Long-term organic marketing support across key platforms with better content rhythm, page maintenance and visibility growth.',
      icon: Share2,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this 6-month plan',
      features: [
        { label: 'Everything from monthly organic handling' },
        { label: 'Longer content calendar planning' },
        { label: 'Stronger brand consistency across platforms' },
        { label: 'Better reporting and content learning cycle' },
        { label: 'Improved SEO article continuity' },
        { label: 'Monthly strategy review support' },
      ],
      addons: [
        { label: 'Brand tone framework', price: 'Included', info: organicLongTermTooltip },
        { label: 'Content bank planning', price: 'Included', info: organicLongTermTooltip },
        { label: 'Extended reporting support', price: 'Included', info: organicLongTermTooltip },
        { label: 'Campaign creative alignment', price: 'Included', info: organicLongTermTooltip },
        { label: 'Monthly strategy review', price: 'Included', info: organicLongTermTooltip },
      ],
    },
  ],
  Yearly: [
    {
      id: 'meta-ads-yearly',
      category: 'Meta Ads',
      name: 'Meta Ads Growth',
      subtitle: 'For brands that want a full-year performance growth partner',
      priceLabel: '₹50,000',
      priceSuffix: '/ year',
      billingNote: '12-month management plan',
      description:
        'Yearly Meta Ads engagement for sustained campaign learning, creative iteration and stronger conversion efficiency.',
      icon: Megaphone,
      accent: 'from-[#0f172a] via-[#1d4ed8] to-[#2563eb]',
      iconClassName: 'text-white',
      featured: true,
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this yearly plan',
      features: [
        { label: 'Everything from 6-month Meta Ads handling' },
        { label: 'Full-year campaign learning and scale support' },
        { label: 'Stronger creative testing cadence' },
        { label: 'More stable cost optimization over time' },
        { label: 'Retargeting and audience expansion layer' },
        { label: 'High-priority optimization support' },
      ],
      addons: [
        { label: 'CRM support free', price: 'Included', info: metaLongTermTooltip },
        { label: 'Advanced analytics', price: 'Included', info: metaLongTermTooltip },
        { label: 'Bidding refinement', price: 'Included', info: metaLongTermTooltip },
        { label: 'Remarketing setup', price: 'Included', info: metaLongTermTooltip },
        { label: 'Brand authorization and agency access', price: 'Included', info: metaLongTermTooltip },
      ],
    },
    {
      id: 'google-ads-yearly',
      category: 'Google Ads',
      name: 'Google Ads Performance',
      subtitle: 'For brands that want strong search continuity and disciplined conversion scaling',
      priceLabel: '₹98,000',
      priceSuffix: '/ year',
      billingNote: 'Setup included in this yearly plan',
      description:
        'Yearly Google Ads engagement with longer tracking maturity, stronger search learning and performance management depth.',
      icon: Search,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this yearly plan',
      features: [
        { label: 'Everything from 6-month Google Ads handling' },
        { label: 'Long-cycle account cleanup and scaling' },
        { label: 'Search-intent learning across seasons' },
        { label: 'Higher-quality optimization over time' },
        { label: 'Better conversion and bid stability' },
        { label: 'Priority performance review rhythm' },
      ],
      addons: [
        { label: 'CRM support free', price: 'Included', info: googleLongTermTooltip },
        { label: 'Advanced analytics', price: 'Included', info: googleLongTermTooltip },
        { label: 'Bidding refinement', price: 'Included', info: googleLongTermTooltip },
        { label: 'Remarketing support', price: 'Included', info: googleLongTermTooltip },
        { label: 'Brand account access support', price: 'Included', info: googleLongTermTooltip },
      ],
    },
    {
      id: 'linkedin-ads-yearly',
      category: 'LinkedIn Ads',
      name: 'LinkedIn B2B Ads',
      subtitle: 'For B2B teams that need long-term demand generation and audience maturity',
      priceLabel: '₹1,56,000',
      priceSuffix: '/ year',
      billingNote: '12-month B2B demand-gen plan',
      description:
        'Yearly LinkedIn Ads plan for B2B companies that want better targeting accuracy, lead quality and message testing over time.',
      icon: Target,
      accent: 'from-slate-950 via-cyan-800 to-sky-600',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this yearly plan',
      features: [
        { label: 'Everything from 6-month LinkedIn Ads handling' },
        { label: 'Long-term audience and offer testing' },
        { label: 'Better CPL and lead-quality learning' },
        { label: 'More mature retargeting structure' },
        { label: 'Extended nurture and form strategy support' },
        { label: 'Priority B2B reporting and optimization' },
      ],
      addons: [
        { label: 'CRM handoff support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Advanced reporting layer', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Audience refinement', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Lead-quality workflow support', price: 'Included', info: linkedinLongTermTooltip },
        { label: 'Brand asset and access support', price: 'Included', info: linkedinLongTermTooltip },
      ],
    },
    {
      id: 'organic-marketing-yearly',
      category: 'Organic Marketing',
      name: 'Social Media + SEO',
      subtitle: 'For brands that want the lowest long-term handling cost and consistent content presence',
      priceLabel: '₹78,000',
      priceSuffix: '/ year',
      billingNote: '12-month handling plan',
      description:
        'Yearly social and organic handling plan for brands that want continuity across platforms, SEO writing and stronger page hygiene.',
      icon: Share2,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      secondarySectionHeading: 'Included in this yearly plan',
      features: [
        { label: 'Everything from 6-month organic handling' },
        { label: 'Long-term content planning and batching' },
        { label: 'Better brand consistency over time' },
        { label: 'Improved reporting and visibility learning' },
        { label: 'Ongoing SEO article continuity' },
        { label: 'Priority monthly strategy review' },
      ],
      addons: [
        { label: 'Brand tone framework', price: 'Included', info: organicLongTermTooltip },
        { label: 'Content bank planning', price: 'Included', info: organicLongTermTooltip },
        { label: 'Extended reporting support', price: 'Included', info: organicLongTermTooltip },
        { label: 'Campaign creative alignment', price: 'Included', info: organicLongTermTooltip },
        { label: 'Monthly strategy review', price: 'Included', info: organicLongTermTooltip },
      ],
    },
  ],
};

const maintenancePlansByDuration: Record<MaintenanceDurationTab, Plan[]> = {
  Monthly: [
    {
      id: 'website-app-maintenance-monthly',
      category: 'Maintenance Plan',
      name: 'Website & Mobile App Maintenance',
      subtitle: 'Keep your website or mobile application secure, updated, and running smoothly',
      priceLabel: '₹5,000',
      priceSuffix: '/ month',
      billingNote: 'Starting monthly maintenance fee',
      description:
        'Routine maintenance plan for websites and mobile apps that need regular updates, security care and smooth operation.',
      icon: Smartphone,
      accent: 'from-slate-950 via-slate-900 to-blue-900',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      featured: true,
      features: [
        { label: 'Regular website and app updates' },
        { label: 'Bug fixes and issue resolution' },
        { label: 'Security monitoring' },
        { label: 'Performance optimization' },
        { label: 'Backup management' },
        { label: 'Server health monitoring' },
        { label: 'SSL and domain support' },
        { label: 'Content and image updates (limited)' },
        { label: 'Database optimization' },
        { label: 'Plugin and dependency updates' },
        { label: 'Basic technical support' },
        { label: 'Monthly performance report' },
      ],
      addons: [
        { label: 'Emergency fix window', price: '₹3,000+', info: 'For urgent issue handling outside the regular maintenance cycle.' },
        { label: 'Landing page redesign', price: '₹8,000+', info: 'Useful when support expands into conversion improvements or layout refresh work.' },
      ],
    },
    {
      id: 'crm-marketplace-maintenance-monthly',
      category: 'Maintenance Plan',
      name: 'CRM & Marketplace Maintenance',
      subtitle: 'Designed for large platforms requiring continuous monitoring and technical support',
      priceLabel: '₹11,000',
      priceSuffix: '/ month',
      billingNote: 'Starting monthly maintenance fee',
      description:
        'Best for larger systems that need ongoing CRM, marketplace, API and server-level operational support.',
      icon: Workflow,
      accent: 'from-emerald-500 via-green-500 to-lime-500',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in website maintenance' },
        { label: 'CRM module maintenance' },
        { label: 'Marketplace feature support' },
        { label: 'API monitoring and fixes' },
        { label: 'Payment gateway support' },
        { label: 'User and admin panel maintenance' },
        { label: 'Database optimization' },
        { label: 'Performance monitoring' },
        { label: 'Security patch updates' },
        { label: 'Bug priority support' },
        { label: 'Server monitoring' },
        { label: 'Backup and recovery' },
        { label: 'Monthly health report' },
        { label: 'Priority technical support' },
      ],
      addons: [
        { label: 'New integration rollout', price: '₹8,000+', info: externalAppIntegrationTooltip },
        { label: 'Cloud printer control', price: '₹45,000+', info: cloudPrinterTooltip },
      ],
    },
    {
      id: 'shared-hosting-monthly',
      category: 'Hosting Plan',
      name: 'Shared Hosting',
      subtitle: 'Perfect for landing pages, portfolios, business websites, company profile websites and blogs',
      priceLabel: '₹199',
      priceSuffix: '/ month',
      billingNote: 'Static website hosting',
      description:
        'Simple and affordable hosting for static websites that need uptime, SSL, backups and dependable performance.',
      icon: Globe,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'PHP support' },
        { label: 'SSL certificate' },
        { label: 'Email accounts' },
        { label: 'Daily backup' },
        { label: 'SSD storage' },
        { label: 'Free website migration' },
        { label: 'Control panel' },
        { label: '99.9% uptime' },
        { label: 'High-speed servers' },
        { label: 'CDN support' },
        { label: 'Basic security protection' },
      ],
      addons: [
        { label: 'Priority migration support', price: '₹1,500+', info: 'For more complex DNS, migration or launch assistance needs.' },
      ],
    },
    {
      id: 'vps-hosting-monthly',
      category: 'Hosting Plan',
      name: 'VPS Hosting',
      subtitle: 'Perfect for e-commerce, CRM, marketplace, SaaS, MERN and frontend + backend hosting',
      priceLabel: '₹799',
      priceSuffix: '/ month',
      billingNote: 'Dynamic app hosting',
      description:
        'Dedicated hosting environment for modern applications that need app deployment control, monitoring and performance.',
      icon: Store,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Node.js support' },
        { label: 'Express.js support' },
        { label: 'React / Next.js support' },
        { label: 'MongoDB support' },
        { label: 'PHP support' },
        { label: 'Frontend and backend deployment' },
        { label: 'PM2 process manager' },
        { label: 'SSL certificate' },
        { label: 'Daily backup' },
        { label: 'Dedicated application environment' },
        { label: 'High performance SSD storage' },
        { label: 'CDN support' },
        { label: 'Firewall protection' },
        { label: 'Auto restart' },
        { label: 'Server monitoring' },
        { label: '99.9% uptime' },
        { label: 'Free deployment assistance' },
      ],
      addons: [
        { label: 'Advanced deployment support', price: '₹4,000+', info: 'For multi-service deployment, environment separation or stronger release workflows.' },
      ],
    },
  ],
  '6 Months': [
    {
      id: 'website-app-maintenance-6m',
      category: 'Maintenance Plan',
      name: 'Website & Mobile App Maintenance',
      subtitle: 'Longer-term maintenance with lower total cost and stronger continuity',
      priceLabel: '₹27,000',
      priceSuffix: '/ 6 months',
      billingNote: 'Save ₹3,000',
      description:
        '6-month support cycle for teams that want continuous fixes, updates and monitoring at a lower total cost.',
      icon: Smartphone,
      accent: 'from-slate-950 via-slate-900 to-blue-900',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      featured: true,
      features: [
        { label: 'Everything in monthly website and app maintenance' },
        { label: 'Lower total cost than monthly billing' },
        { label: 'Stronger continuity for updates and monitoring' },
      ],
      addons: [{ label: 'Emergency fix window', price: '₹3,000+', info: 'For urgent issue handling outside the regular maintenance cycle.' }],
    },
    {
      id: 'crm-marketplace-maintenance-6m',
      category: 'Maintenance Plan',
      name: 'CRM & Marketplace Maintenance',
      subtitle: '6-month support plan for larger and more sensitive systems',
      priceLabel: '₹59,400',
      priceSuffix: '/ 6 months',
      billingNote: 'Save ₹6,600',
      description:
        '6-month continuity plan for API-heavy systems, CRM workflows and marketplace operations.',
      icon: Workflow,
      accent: 'from-emerald-500 via-green-500 to-lime-500',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly CRM and marketplace maintenance' },
        { label: 'Lower total cost than monthly billing' },
        { label: 'Longer support and reporting continuity' },
      ],
      addons: [{ label: 'New integration rollout', price: '₹8,000+', info: externalAppIntegrationTooltip }],
    },
    {
      id: 'shared-hosting-6m',
      category: 'Hosting Plan',
      name: 'Shared Hosting',
      subtitle: '6-month hosting plan for static websites with lower recurring cost',
      priceLabel: '₹1,080',
      priceSuffix: '/ 6 months',
      billingNote: 'Save up to 10%',
      description:
        '6-month shared hosting option for brochure, portfolio and landing-page-style websites.',
      icon: Globe,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly shared hosting' },
        { label: 'Lower total cost than monthly billing' },
        { label: 'Better backup and uptime continuity' },
      ],
      addons: [{ label: 'Priority migration support', price: '₹1,500+', info: 'For more complex DNS, migration or launch assistance needs.' }],
    },
    {
      id: 'vps-hosting-6m',
      category: 'Hosting Plan',
      name: 'VPS Hosting',
      subtitle: '6-month hosting plan for dynamic and performance-heavy applications',
      priceLabel: '₹4,320',
      priceSuffix: '/ 6 months',
      billingNote: 'Save up to 10%',
      description:
        '6-month VPS plan for e-commerce, CRM, SaaS and marketplace applications that need continuity.',
      icon: Store,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly VPS hosting' },
        { label: 'Lower total cost than monthly billing' },
        { label: 'Stable environment continuity across releases' },
      ],
      addons: [{ label: 'Advanced deployment support', price: '₹4,000+', info: 'For multi-service deployment, environment separation or stronger release workflows.' }],
    },
  ],
  '12 Months': [
    {
      id: 'website-app-maintenance-12m',
      category: 'Maintenance Plan',
      name: 'Website & Mobile App Maintenance',
      subtitle: 'Best value annual maintenance for stable business websites and apps',
      priceLabel: '₹48,000',
      priceSuffix: '/ year',
      billingNote: 'Save ₹12,000',
      description:
        'Best-value annual maintenance plan for brands that want year-round support and lower long-term cost.',
      icon: Smartphone,
      accent: 'from-slate-950 via-slate-900 to-blue-900',
      iconClassName: 'text-white',
      primaryAction: 'Purchase Now',
      featured: true,
      features: [
        { label: 'Everything in monthly website and app maintenance' },
        { label: 'Best annual support value' },
        { label: 'Year-round continuity for updates and issue handling' },
      ],
      addons: [{ label: 'Emergency fix window', price: '₹3,000+', info: 'For urgent issue handling outside the regular maintenance cycle.' }],
    },
    {
      id: 'crm-marketplace-maintenance-12m',
      category: 'Maintenance Plan',
      name: 'CRM & Marketplace Maintenance',
      subtitle: 'Best value annual support for large operational systems',
      priceLabel: '₹105,600',
      priceSuffix: '/ year',
      billingNote: 'Save ₹26,400',
      description:
        'Annual support plan for CRM and marketplace systems that need sustained technical continuity and priority care.',
      icon: Workflow,
      accent: 'from-emerald-500 via-green-500 to-lime-500',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly CRM and marketplace maintenance' },
        { label: 'Best annual support value' },
        { label: 'Year-round continuity for APIs, users and admin operations' },
      ],
      addons: [{ label: 'New integration rollout', price: '₹8,000+', info: externalAppIntegrationTooltip }],
    },
    {
      id: 'shared-hosting-12m',
      category: 'Hosting Plan',
      name: 'Shared Hosting',
      subtitle: 'Best value annual hosting for landing pages, profile sites and blogs',
      priceLabel: '₹1,800',
      priceSuffix: '/ year',
      billingNote: 'Save ₹588',
      description:
        'Annual shared hosting option for static websites that want the best long-term value.',
      icon: Globe,
      accent: 'from-white via-sky-50 to-violet-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly shared hosting' },
        { label: 'Best annual hosting value' },
        { label: 'Year-round SSL, backup and uptime continuity' },
      ],
      addons: [{ label: 'Priority migration support', price: '₹1,500+', info: 'For more complex DNS, migration or launch assistance needs.' }],
    },
    {
      id: 'vps-hosting-12m',
      category: 'Hosting Plan',
      name: 'VPS Hosting',
      subtitle: 'Best value annual hosting for apps, APIs, CRM and marketplace platforms',
      priceLabel: '₹7,000',
      priceSuffix: '/ year',
      billingNote: 'Save ₹2,588',
      description:
        'Annual VPS hosting plan for performance-focused application stacks that need stronger continuity.',
      icon: Store,
      accent: 'from-white via-orange-50 to-amber-50',
      primaryAction: 'Purchase Now',
      features: [
        { label: 'Everything in monthly VPS hosting' },
        { label: 'Best annual hosting value' },
        { label: 'Long-term deployment and monitoring continuity' },
      ],
      addons: [{ label: 'Advanced deployment support', price: '₹4,000+', info: 'For multi-service deployment, environment separation or stronger release workflows.' }],
    },
  ],
};

export const plansByBilling: Record<BillingTab, Plan[]> = {
  Development: developmentPlans,
  Marketing: marketingPlansByDuration.Monthly,
  'Maintenance + Hosting': maintenancePlansByDuration.Monthly,
};

export const addOnsByBilling: Record<BillingTab, AddonCard[]> = {
  Development: [
    {
      title: 'WhatsApp Business API Setup',
      description:
        'Free with eligible plan via AiWizChat. External provider setup can be added separately when needed.',
      price: 'Free with plan / ₹5,000 standalone',
      icon: MessageCircleMore,
    },
    {
      title: 'External App Integration',
      description:
        'Connect Google Calendar, Shopify, n8n, Google Meet, Sheets, Excel and other tools into your workflow.',
      price: '₹8,000+ / app',
      icon: Cable,
    },
    {
      title: 'Shipping Panel Integration',
      description:
        'Automatically send new orders to your shipping provider or fulfillment panel with status sync.',
      price: '₹4,000+',
      icon: Truck,
    },
    {
      title: 'Security Access Hardware',
      description:
        'For hardware-based access, verification or device-driven workflow automation in CRM and marketplace setups.',
      price: '₹25,000+',
      icon: ShieldCheck,
    },
    {
      title: 'Cloud Printer Control',
      description:
        'Connect one cloud printer across multiple devices with super-admin controls and centralized print handling.',
      price: '₹45,000+',
      icon: Printer,
    },
    {
      title: 'Digital KYC Verification',
      description:
        'Add verification-ready onboarding workflows for business documents, PAN, GST and bank validation scope.',
      price: 'Custom scope',
      icon: LayoutGrid,
    },
    {
      title: 'AI Sales or Support Agent',
      description:
        'Automate first response, product discovery, support prompts and order-status conversations.',
      price: '₹5,000+',
      icon: Bot,
    },
    {
      title: 'Email Marketing Setup',
      description:
        'Email tool setup, list sync and automated customer follow-up or retention journeys.',
      price: '₹3,000+',
      icon: Mail,
    },
    {
      title: 'Meta Pixel & Conversion Tracking',
      description:
        'Track leads, add to cart, checkout, consultation and purchase events from your campaigns.',
      price: '₹2,000+',
      icon: Search,
    },
  ],
  Marketing: [
    {
      title: 'Landing Page Audit + CRO',
      description:
        'Review landing pages, forms, CTA blocks and user flow to improve ad-to-conversion performance.',
      price: '₹8,000+',
      icon: Globe,
    },
    {
      title: 'CRM + Lead Routing Setup',
      description:
        'Connect ad leads into CRM, WhatsApp or internal sales workflow with cleaner response handling.',
      price: '₹10,000+',
      icon: Workflow,
    },
    {
      title: 'Conversion API / GTM Tracking',
      description:
        'Improve event tracking, ad-platform signal quality and conversion reporting visibility.',
      price: '₹6,000+',
      icon: BarChart3,
    },
    {
      title: 'WhatsApp Follow-up Automation',
      description:
        'Auto-respond to leads, push reminders and route hot prospects into your team faster.',
      price: '₹8,000+',
      icon: MessageCircleMore,
    },
    {
      title: 'Creative Production Pack',
      description:
        'Ad creatives, static designs, hooks and campaign copy support for paid and organic growth.',
      price: '₹7,000+',
      icon: Megaphone,
    },
    {
      title: 'Video Editing / UGC Ad Pack',
      description:
        'Short-form ad editing, reel-style creatives and performance-friendly visual asset support.',
      price: '₹12,000+',
      icon: Share2,
    },
  ],
  'Maintenance + Hosting': [
    {
      title: 'Emergency Fix Window',
      description:
        'Fast-response support for high-priority issues that need immediate investigation and recovery.',
      price: 'Custom',
      icon: ShieldCheck,
    },
    {
      title: 'Monthly Performance Reporting',
      description:
        'Additional reporting layer for uptime, issue trends, support actions and ops visibility.',
      price: '₹4,000+',
      icon: BarChart3,
    },
    {
      title: 'Extra Integration Support',
      description:
        'Add or revise external workflows, APIs or automation connectors during support cycles.',
      price: '₹8,000+',
      icon: Cable,
    },
    {
      title: 'Security Review Pack',
      description:
        'Stronger hardening, permission review and system hygiene beyond routine monthly maintenance.',
      price: '₹5,000+',
      icon: ShieldCheck,
    },
    {
      title: 'Cloud Printer Control',
      description:
        'Centralized print queue and access control layer for operations-heavy systems.',
      price: '₹45,000+',
      icon: Printer,
    },
    {
      title: 'KYC / Verification Expansion',
      description:
        'Extend document verification, seller-validation or business-onboarding workflows over time.',
      price: 'Custom scope',
      icon: LayoutGrid,
    },
  ],
};

export const comparisonConfigsByBilling: Record<BillingTab, ComparisonConfig> = {
  Development: {
    headers: ['Website', 'App', 'CRM', 'Marketplace'],
    rows: [
      { label: 'Storefront UI', values: ['check', 'check', 'dash', 'check'] },
      { label: 'Checkout / Payment Flow', values: ['check', 'check', 'optional', 'check'] },
      { label: 'Order Management', values: ['check', 'optional', 'dash', 'check'] },
      { label: 'Lead / Sales Pipeline', values: ['dash', 'dash', 'check', 'optional'] },
      { label: 'WhatsApp API Ready', values: ['check', 'check', 'check', 'check'] },
      { label: 'Analytics Dashboard', values: ['check', 'check', 'check', 'check'] },
      { label: 'Vendor Management', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Digital KYC Scope', values: ['dash', 'dash', 'optional', 'check'] },
    ],
  },
  Marketing: {
    headers: ['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'Organic Social'],
    rows: [
      { label: 'Paid lead generation', values: ['check', 'check', 'check', 'dash'] },
      { label: 'E-commerce sales support', values: ['check', 'optional', 'dash', 'optional'] },
      { label: 'Search-intent traffic', values: ['dash', 'check', 'dash', 'optional'] },
      { label: 'B2B targeting', values: ['optional', 'optional', 'check', 'optional'] },
      { label: 'Daily platform handling', values: ['dash', 'dash', 'dash', 'check'] },
      { label: 'Remarketing ready', values: ['check', 'check', 'check', 'optional'] },
      { label: 'CRM handoff potential', values: ['optional', 'optional', 'optional', 'optional'] },
      { label: 'SEO article support', values: ['dash', 'dash', 'dash', 'check'] },
    ],
  },
  'Maintenance + Hosting': {
    headers: ['Website & App', 'CRM & Marketplace', 'Shared Hosting', 'VPS Hosting'],
    rows: [
      { label: 'Routine updates', values: ['check', 'check', 'optional', 'optional'] },
      { label: 'Priority bug support', values: ['optional', 'check', 'dash', 'dash'] },
      { label: 'Server / uptime monitoring', values: ['check', 'check', 'check', 'check'] },
      { label: 'SSL and backup support', values: ['check', 'check', 'check', 'check'] },
      { label: 'API or deployment readiness', values: ['optional', 'check', 'dash', 'check'] },
      { label: 'Database optimization', values: ['check', 'check', 'dash', 'optional'] },
      { label: 'Static website fit', values: ['optional', 'dash', 'check', 'dash'] },
      { label: 'Dynamic app fit', values: ['optional', 'check', 'dash', 'check'] },
    ],
  },
};

export const faqs = [
  {
    question: 'Can I purchase even if my website or system is already built?',
    answer:
      'Yes. We can review the current stack and add new modules, integrations or workflow improvements into an existing website, app, CRM, marketplace or marketing workflow.',
  },
  {
    question: 'How does the Marketing tab pricing work?',
    answer:
      'Marketing plans are shown in monthly, half-yearly and yearly formats. Long-term plans include more strategic support such as reporting depth, CRM help, analytics refinement or audience optimization depending on the channel.',
  },
  {
    question: 'How does the Maintenance + Hosting tab pricing work?',
    answer:
      'Maintenance and hosting plans switch between monthly, 6-month and 12-month pricing. Longer terms reduce the effective total cost and are designed for businesses that want continuity and fewer renewal cycles.',
  },
  {
    question: 'Is WhatsApp Business API really free in every eligible plan?',
    answer:
      'The integration setup is included when the plan is purchased through AiWizChat. If you want another provider, the setup charge is ₹5,000. Meta and partner messaging fees remain separate on actual usage.',
  },
  {
    question: 'Are ad spend and platform charges included in marketing plans?',
    answer:
      'No. Ad spend, Meta messaging fees, software subscriptions, platform charges, payment gateway charges and third-party service fees are billed separately by the relevant provider.',
  },
  {
    question: 'Can I start with one plan and expand later?',
    answer:
      'Yes. Many businesses start with one build, one marketing channel or one support plan first, then expand into CRM, automation, app, marketplace or long-term performance management later.',
  },
];

export function getPlansForBilling(
  billing: BillingTab,
  marketingDuration: MarketingDurationTab,
  maintenanceDuration: MaintenanceDurationTab,
): Plan[] {
  if (billing === 'Marketing') {
    return marketingPlansByDuration[marketingDuration];
  }

  if (billing === 'Maintenance + Hosting') {
    return maintenancePlansByDuration[maintenanceDuration];
  }

  return developmentPlans;
}
